import { atom } from 'jotai';

export const userRolesAtom = atom([]);
export const routesAtom = atom([]);

// The logged-in user's own effective permission keys, from GET Bootstrap.
//
// `null` means "not known yet", which is why it is not an empty array: an empty
// array is a real answer (this user holds nothing) and the shell may render on
// it, while null must hold the shell back — a control that renders before its
// key arrives appears and then vanishes. Read through `useHasPermission`.
export const userPermissionsAtom = atom(null);

// The same keys, lower-cased into a Set. Derived so it is computed once per
// change and shared by every consumer, rather than rebuilt in each hook call.
//
// Lower-cased because the server compares keys OrdinalIgnoreCase throughout,
// while what it returns is whatever spelling the `Permissions` row happens to
// hold — so an exact-match check here would silently disagree with the
// authorisation that actually runs.
export const userPermissionKeysAtom = atom((get) => {
  const keys = get(userPermissionsAtom);
  return keys === null ? null : new Set(keys.map((key) => key.toLowerCase()));
});

// Install-scoped module capability flags, from GET Bootstrap. `null` until known.
//
// Per installation, not per user — an operator edits them in SettingsPage and they
// take effect on the next read, so they are not permissions and must not be used as
// such: anything that protects data belongs in `userPermissionsAtom` and is enforced
// server-side. Read through `useModuleFlag`.
//
// Replaces `window.databeasy`. The object survives for API_URL, AUTH_SCHEME and
// PROVIDER — the client needs those before it can call anything — but no capability
// is decided there any more.
export const moduleFlagsAtom = atom(null);

// The domain a new user's address is built under, from GET Bootstrap. `null` until known.
//
// Per tenant, not per installation: one application container serves every tenant, so a value
// injected at container start by `env.sh` is the same for all of them. An operator sets it per
// tenant on the control-plane dashboard, under `email.domain`.
export const emailDomainAtom = atom(null);
export const authCheckedAtom = atom(false);
export const activityOutcomesAtom = atom([]);
export const activityTypesAtom = atom([]);
export const themeAtom = atom(localStorage.getItem('theme') || 'light');

export const allOutcomesAtom = atom([]);

export const outcomesAtom = atom([]);

/**
 * The VoIP devices the logged user's account offers, held for the session.
 *
 * Null until they have been asked for. The account menu is remounted whenever the navbar is - the
 * layout editor puts its own toolbar there - and asking again would re-raise the prompt that offers a
 * new device when the stored one is missing.
 */
export const voIPDevicesAtom = atom(null);

export const lpgLinksAtom = atom([]);

export const selectedCustomerIdAtom = atom('');

export const additionalCustomerDataAtom = atom({});

export const endedCallDataAtom = atom(null);

export const userDataAtom = atom(null);

export const userBranchesAtom = atom(null);

export const selectedBranchAtom = atom(null);

export const searchValuesAtom = atom(null);

export const currentDateAtom = atom(new Date());

export const loadingCounterAtom = atom(0);

export const openSnackbarAdviseAtom = atom(null);

export const occupationsAtom = atom(null);

export const productsAtom = atom(null);

export const practiceProductAtom = atom(null);

export const alertDialogAtom = atom(null);

export const selectedLeadIdAtom = atom('');

export const reloadLeadsAtom = atom(false);

export const reloadAtom = atom(false);
export const maxDistanceAtom = atom(150);

// The layout being edited in place, or null when nothing is being edited.
//
// Editing a detail layout is a mode of the page itself: the sidebar becomes the widget menu, the
// navbar becomes the layout's toolbar, and the page becomes the canvas. Those three are siblings in
// the shell, so the session they share is held here rather than passed. Written through
// `useLayoutEdit`.
export const layoutEditAtom = atom(null);

// Saved layouts by section, as the rows the API returns: `{ [sectionName]: rows }`.
//
// A section is fetched once, with every surface in the one call, and each surface picks its own row
// out of it. A section absent from here has not been asked for yet; read through
// `useSectionLayout`, which is the only thing that writes it.
export const sectionLayoutsAtom = atom({});

// The names of the sections the server knows, which is what tells a list page from any other page a
// customer can be opened from. Null until asked, false when the answer could not be had.
export const waveSectionNamesAtom = atom(null);

// The saved widget presets, as the rows the API returns, or null until they have been asked for.
//
// Read through `useWidgetPresets`, which is the only thing that writes it: a page renders a layout
// that names one and the editor offers them, and both read the same set.
export const widgetPresetsAtom = atom(null);
